import React from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
} from '@chakra-ui/react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SIDEBAR_SECTIONS } from '../constants/navigation';
import {
  ChevronLeft,
  ChevronRight,
  LogOut,
  Flame,
  Activity,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const MotionBox = motion.create(Box);
const MotionFlex = motion.create(Flex);

export const Sidebar = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box
      as="aside"
      w={isCollapsed ? '78px' : '264px'}
      h="100vh"
      position="sticky"
      top="0"
      bg="white"
      _dark={{
        bg: '#0E131F',
        borderColor: 'rgba(255, 255, 255, 0.07)',
        boxShadow: '4px 0 28px rgba(0, 0, 0, 0.45)',
      }}
      borderRight="1px solid"
      borderColor="rgba(226, 232, 240, 0.8)"
      transition="width 0.28s cubic-bezier(0.4, 0, 0.2, 1)"
      zIndex="100"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      boxShadow="4px 0 24px -2px rgba(15, 23, 42, 0.03)"
      userSelect="none"
    >
      {/* Brand Header */}
      <Box px={isCollapsed ? 3 : 4} py={4}>
        <Flex
          align="center"
          justify={isCollapsed ? 'center' : 'space-between'}
          position="relative"
        >
          {/* Logo & Brand Name */}
          <Flex
            as={NavLink}
            to="/dashboard"
            align="center"
            gap={3}
            textDecoration="none"
            _hover={{ textDecoration: 'none' }}
          >
            {/* Glowing Gradient Squircle Logo */}
            <Flex
              w="42px"
              h="42px"
              minW="42px"
              borderRadius="14px"
              bgGradient="linear(to-br, #FF3366, #7000FF)"
              align="center"
              justify="center"
              boxShadow="0 4px 16px -2px rgba(255, 51, 102, 0.45)"
              position="relative"
              overflow="hidden"
              cursor="pointer"
              transition="transform 0.2s"
              _hover={{ transform: 'scale(1.04)' }}
            >
              <Box
                position="absolute"
                inset="0"
                bgGradient="linear(to-t, transparent 40%, rgba(255,255,255,0.3) 100%)"
              />
              <Icon as={Flame} color="white" w={5} h={5} fill="white" />
            </Flex>

            {/* Brand Title & Badge (when expanded) */}
            <AnimatePresence>
              {!isCollapsed && (
                <MotionBox
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.18 }}
                  overflow="hidden"
                  whiteSpace="nowrap"
                >
                  <HStack spacing={1.5} align="center">
                    <Text
                      fontSize="17px"
                      fontWeight="900"
                      letterSpacing="-0.5px"
                      bgGradient="linear(to-r, #FF3366, #8B5CF6)"
                      bgClip="text"
                    >
                      Cliked
                    </Text>
                    <Badge
                      px={1.5}
                      py={0.2}
                      borderRadius="6px"
                      fontSize="9px"
                      fontWeight="800"
                      letterSpacing="0.5px"
                      bg="rgba(112, 0, 255, 0.1)"
                      color="#7000FF"
                      _dark={{
                        bg: 'rgba(139, 92, 246, 0.2)',
                        color: '#A78BFA',
                      }}
                    >
                      ADMIN
                    </Badge>
                  </HStack>
                  <Text
                    fontSize="10px"
                    fontWeight="700"
                    color="gray.400"
                    _dark={{ color: 'gray.500' }}
                    letterSpacing="0.8px"
                    textTransform="uppercase"
                  >
                    Dating Ecosystem
                  </Text>
                </MotionBox>
              )}
            </AnimatePresence>
          </Flex>

          {/* Sidebar Collapse Toggle Button */}
          {!isCollapsed && (
            <Tooltip
              label="Collapse Sidebar"
              placement="right"
              hasArrow
              borderRadius="8px"
              fontSize="xs"
            >
              <IconButton
                size="xs"
                w="26px"
                h="26px"
                icon={<ChevronLeft size={15} />}
                onClick={onToggle}
                aria-label="Collapse Sidebar"
                borderRadius="8px"
                variant="ghost"
                color="gray.500"
                _hover={{
                  bg: 'gray.100',
                  color: 'gray.800',
                  _dark: { bg: 'rgba(255,255,255,0.08)', color: 'white' },
                }}
              />
            </Tooltip>
          )}
        </Flex>

        {/* Collapsed Toggle Button below icon */}
        {isCollapsed && (
          <Flex justify="center" mt={3}>
            <Tooltip
              label="Expand Sidebar"
              placement="right"
              hasArrow
              borderRadius="8px"
              fontSize="xs"
            >
              <IconButton
                size="xs"
                w="26px"
                h="26px"
                icon={<ChevronRight size={15} />}
                onClick={onToggle}
                aria-label="Expand Sidebar"
                borderRadius="8px"
                variant="ghost"
                color="gray.500"
                _hover={{
                  bg: 'gray.100',
                  color: 'gray.800',
                  _dark: { bg: 'rgba(255,255,255,0.08)', color: 'white' },
                }}
              />
            </Tooltip>
          </Flex>
        )}
      </Box>

      <Divider opacity={0.6} borderColor="gray.100" _dark={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      {/* Structured Categorized Menu List */}
      <Box
        flex="1"
        overflowY="auto"
        py={3}
        px={isCollapsed ? 2 : 3}
        sx={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(148, 163, 184, 0.25)',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(148, 163, 184, 0.45)',
          },
        }}
      >
        <VStack spacing={4} align="stretch">
          {SIDEBAR_SECTIONS.map((section, sIdx) => (
            <Box key={section.title || sIdx}>
              {/* Section Header */}
              {!isCollapsed ? (
                <Text
                  px={3}
                  pb={1.5}
                  fontSize="10px"
                  fontWeight="800"
                  color="gray.400"
                  _dark={{ color: 'gray.500' }}
                  letterSpacing="1px"
                  textTransform="uppercase"
                >
                  {section.title}
                </Text>
              ) : sIdx > 0 ? (
                <Box
                  my={2}
                  mx="auto"
                  w="24px"
                  h="1px"
                  bg="gray.200"
                  _dark={{ bg: 'rgba(255,255,255,0.08)' }}
                />
              ) : null}

              {/* Section Nav Items */}
              <VStack spacing={1} align="stretch">
                {section.items.map((item) => {
                  const isActive =
                    location.pathname === item.path ||
                    (item.path !== '/dashboard' &&
                      location.pathname.startsWith(item.path));
                  const IconComp = item.icon;

                  const linkContent = (
                    <Flex
                      align="center"
                      justify={isCollapsed ? 'center' : 'space-between'}
                      px={isCollapsed ? 2 : 3}
                      py={2.5}
                      borderRadius="12px"
                      position="relative"
                      bg={
                        isActive
                          ? 'rgba(255, 51, 102, 0.08)'
                          : 'transparent'
                      }
                      color={
                        isActive
                          ? '#FF3366'
                          : 'gray.600'
                      }
                      _dark={{
                        bg: isActive
                          ? 'rgba(255, 51, 102, 0.16)'
                          : 'transparent',
                        color: isActive ? '#FF6584' : 'gray.400',
                      }}
                      fontWeight={isActive ? '700' : '500'}
                      fontSize="13px"
                      cursor="pointer"
                      transition="all 0.18s cubic-bezier(0.4, 0, 0.2, 1)"
                      _hover={{
                        bg: isActive
                          ? 'rgba(255, 51, 102, 0.12)'
                          : 'gray.50',
                        color: isActive ? '#FF3366' : 'gray.900',
                        _dark: {
                          bg: isActive
                            ? 'rgba(255, 51, 102, 0.22)'
                            : 'rgba(255, 255, 255, 0.04)',
                          color: isActive ? '#FF6584' : 'white',
                        },
                        transform: isCollapsed ? 'none' : 'translateX(2px)',
                      }}
                    >
                      {/* Active Indicator Bar */}
                      {isActive && (
                        <MotionBox
                          layoutId="activeSidebarIndicator"
                          position="absolute"
                          left="0"
                          w="3.5px"
                          h="20px"
                          bgGradient="linear(to-b, #FF3366, #8B5CF6)"
                          borderRadius="0 4px 4px 0"
                          boxShadow="0 0 10px rgba(255, 51, 102, 0.6)"
                          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                        />
                      )}

                      <Flex align="center" gap={3}>
                        <Flex
                          w="28px"
                          h="28px"
                          align="center"
                          justify="center"
                          borderRadius="8px"
                          bg={
                            isActive
                              ? 'rgba(255, 51, 102, 0.1)'
                              : 'transparent'
                          }
                          _dark={{
                            bg: isActive
                              ? 'rgba(255, 51, 102, 0.2)'
                              : 'transparent',
                          }}
                          transition="all 0.2s"
                        >
                          <Icon
                            as={IconComp}
                            w="17px"
                            h="17px"
                            color={
                              isActive
                                ? '#FF3366'
                                : 'inherit'
                            }
                            _dark={{
                              color: isActive ? '#FF6584' : 'inherit',
                            }}
                          />
                        </Flex>

                        {!isCollapsed && (
                          <Text
                            noOfLines={1}
                            letterSpacing="-0.1px"
                            lineHeight="short"
                          >
                            {item.label}
                          </Text>
                        )}
                      </Flex>

                      {!isCollapsed && item.badgeColor && (
                        <Badge
                          colorScheme={item.badgeColor}
                          borderRadius="full"
                          px={2}
                          py={0.2}
                          fontSize="9px"
                          fontWeight="800"
                        >
                          Active
                        </Badge>
                      )}
                    </Flex>
                  );

                  return isCollapsed ? (
                    <Tooltip
                      key={item.id}
                      label={item.label}
                      placement="right"
                      hasArrow
                      borderRadius="8px"
                      px={3}
                      py={1.5}
                      fontSize="xs"
                      fontWeight="600"
                      bg="gray.900"
                      color="white"
                      _dark={{ bg: 'white', color: 'gray.900' }}
                    >
                      <NavLink
                        to={item.path}
                        style={{ textDecoration: 'none' }}
                      >
                        {linkContent}
                      </NavLink>
                    </Tooltip>
                  ) : (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      style={{ textDecoration: 'none' }}
                    >
                      {linkContent}
                    </NavLink>
                  );
                })}
              </VStack>
            </Box>
          ))}
        </VStack>
      </Box>

      {/* System Status Micro-Widget (Expanded Mode) */}
      {!isCollapsed && (
        <Box px={3} pt={2} pb={1}>
          <Box
            p={2.5}
            borderRadius="12px"
            bg="gray.50"
            _dark={{
              bg: 'rgba(255, 255, 255, 0.03)',
              borderColor: 'rgba(255, 255, 255, 0.05)',
            }}
            border="1px solid"
            borderColor="gray.100"
          >
            <Flex align="center" justify="space-between">
              <HStack spacing={2}>
                <Box
                  w="7px"
                  h="7px"
                  borderRadius="full"
                  bg="emerald.400"
                  boxShadow="0 0 8px #10B981"
                  animation="pulse 2s infinite"
                />
                <Text
                  fontSize="11px"
                  fontWeight="700"
                  color="gray.600"
                  _dark={{ color: 'gray.300' }}
                >
                  Live Server
                </Text>
              </HStack>
              <Badge
                variant="subtle"
                colorScheme="green"
                fontSize="9px"
                borderRadius="6px"
                px={1.5}
              >
                99.9% Up
              </Badge>
            </Flex>
          </Box>
        </Box>
      )}

      <Divider opacity={0.6} borderColor="gray.100" _dark={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      {/* User Profile Footer */}
      <Box p={isCollapsed ? 2 : 3}>
        {!isCollapsed ? (
          <Flex
            align="center"
            justify="space-between"
            bg="gray.50"
            _dark={{
              bg: 'rgba(255, 255, 255, 0.03)',
              borderColor: 'rgba(255, 255, 255, 0.05)',
            }}
            border="1px solid"
            borderColor="gray.100"
            p={2.5}
            borderRadius="14px"
            transition="all 0.2s"
            _hover={{
              borderColor: 'gray.200',
              _dark: { borderColor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <Flex
              align="center"
              gap={2.5}
              cursor="pointer"
              onClick={() => navigate('/settings')}
              overflow="hidden"
              flex="1"
            >
              <Box position="relative">
                <Avatar
                  size="sm"
                  name={user?.name || 'Victoria Vance'}
                  src={
                    user?.avatar ||
                    user?.avatarUrl ||
                    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80'
                  }
                  border="2px solid"
                  borderColor="white"
                  _dark={{ borderColor: 'gray.800' }}
                />
                <Box
                  position="absolute"
                  bottom="0"
                  right="0"
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg="green.400"
                  border="1.5px solid white"
                  _dark={{ border: '1.5px solid #0E131F' }}
                />
              </Box>
              <Box overflow="hidden">
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  noOfLines={1}
                  color="gray.800"
                  _dark={{ color: 'white' }}
                >
                  {user?.name || 'Victoria Vance'}
                </Text>
                <Text
                  fontSize="10px"
                  fontWeight="600"
                  color="gray.400"
                  noOfLines={1}
                >
                  {user?.role || 'Super Admin'}
                </Text>
              </Box>
            </Flex>

            <Tooltip
              label="Sign Out"
              placement="top"
              hasArrow
              borderRadius="8px"
              fontSize="xs"
            >
              <IconButton
                size="xs"
                w="28px"
                h="28px"
                colorScheme="red"
                variant="ghost"
                icon={<LogOut size={15} />}
                onClick={handleLogout}
                aria-label="Logout"
                borderRadius="8px"
                _hover={{
                  bg: 'red.50',
                  color: 'red.600',
                  _dark: { bg: 'rgba(239, 68, 68, 0.15)', color: 'red.400' },
                }}
              />
            </Tooltip>
          </Flex>
        ) : (
          <VStack spacing={2} align="center">
            <Tooltip
              label={`${user?.name || 'Victoria Vance'} (Profile)`}
              placement="right"
              hasArrow
              borderRadius="8px"
              fontSize="xs"
            >
              <Box
                position="relative"
                cursor="pointer"
                onClick={() => navigate('/settings')}
              >
                <Avatar
                  size="sm"
                  name={user?.name || 'Victoria Vance'}
                  src={
                    user?.avatar ||
                    user?.avatarUrl ||
                    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80'
                  }
                  border="2px solid"
                  borderColor="white"
                  _dark={{ borderColor: 'gray.800' }}
                />
                <Box
                  position="absolute"
                  bottom="0"
                  right="0"
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg="green.400"
                  border="1.5px solid white"
                  _dark={{ border: '1.5px solid #0E131F' }}
                />
              </Box>
            </Tooltip>

            <Tooltip
              label="Sign Out"
              placement="right"
              hasArrow
              borderRadius="8px"
              fontSize="xs"
            >
              <IconButton
                w="32px"
                h="32px"
                size="xs"
                colorScheme="red"
                variant="ghost"
                icon={<LogOut size={16} />}
                onClick={handleLogout}
                aria-label="Logout"
                borderRadius="10px"
                _hover={{
                  bg: 'red.50',
                  color: 'red.600',
                  _dark: { bg: 'rgba(239, 68, 68, 0.15)', color: 'red.400' },
                }}
              />
            </Tooltip>
          </VStack>
        )}
      </Box>
    </Box>
  );
};
